import { useCallback, useEffect, useState } from 'react'
import { Button, Code, Group, ScrollArea, SimpleGrid, Stack, Table, Text } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { AppModal, InlineLoader } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type { OpenAIResourceAssistant, OpenAIResourceVectorStore, OpenAIResourcesResponse, VLLMResourcesResponse } from '@/types/api'

type Props = {
  projectId: string
  provider: string
  canEdit: boolean
  onResourcesChanged: () => void
}

function idShort(id: string, n = 20) {
  if (!id) return '—'
  return id.length <= n ? id : `${id.slice(0, n)}…`
}

export function AiResourcesTab({ projectId, provider, canEdit, onResourcesChanged }: Props) {
  const p = provider.toLowerCase()
  const isVllm = p === 'vllm'
  const isOpenAI = p === 'openai'
  const resourcesSupported = isVllm || isOpenAI
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openaiData, setOpenaiData] = useState<OpenAIResourcesResponse | null>(null)
  const [vllmData, setVllmData] = useState<VLLMResourcesResponse | null>(null)
  const [detailsJson, setDetailsJson] = useState<string | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (isVllm) {
        const data = await api.vllmResources(projectId)
        setVllmData(data)
        setOpenaiData(null)
      } else if (isOpenAI) {
        const data = await api.openAIResources(projectId)
        setOpenaiData(data)
        setVllmData(null)
      } else {
        setOpenaiData(null)
        setVllmData(null)
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Не удалось загрузить'
      setError(msg)
      setOpenaiData(null)
      setVllmData(null)
    } finally {
      setLoading(false)
    }
  }, [projectId, isVllm, isOpenAI])

  useEffect(() => {
    void load()
  }, [load])

  const notifyErr = (e: unknown) =>
    showNotification({
      title: 'Ошибка',
      message: e instanceof ApiError ? e.message : 'Операция не удалась',
      color: 'red',
    })

  const deleteOpenAIvs = (vsId: string) =>
    openConfirmModal({
      title: 'Удалить векторное хранилище?',
      children: <Text size="sm">ID: {idShort(vsId, 40)}. Действие нельзя отменить.</Text>,
      confirmProps: { color: 'red' },
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      onConfirm: () =>
        void api
          .deleteOpenAIVectorStore(projectId, vsId)
          .then(() => {
            showNotification({ title: 'Удалено', message: 'Векторное хранилище удалено', color: 'green' })
            onResourcesChanged()
            return load()
          })
          .catch(notifyErr),
    })

  const deleteOpenAIasst = (assistantId: string) =>
    openConfirmModal({
      title: 'Удалить ассистента?',
      children: <Text size="sm">ID: {idShort(assistantId, 40)}. Действие нельзя отменить.</Text>,
      confirmProps: { color: 'red' },
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      onConfirm: () =>
        void api
          .deleteOpenAIAssistantResource(projectId, assistantId)
          .then(() => {
            showNotification({ title: 'Удалено', message: 'Ассистент удалён', color: 'green' })
            onResourcesChanged()
            return load()
          })
          .catch(notifyErr),
    })

  const deleteVllmVs = (vsId: string) =>
    openConfirmModal({
      title: 'Удалить векторное хранилище (vLLM)?',
      children: <Text size="sm">ID: {idShort(vsId, 40)}</Text>,
      confirmProps: { color: 'red' },
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      onConfirm: () =>
        void api
          .deleteVLLMVectorStore(projectId, vsId)
          .then(() => {
            showNotification({ title: 'Удалено', message: '', color: 'green' })
            onResourcesChanged()
            return load()
          })
          .catch(notifyErr),
    })

  const deleteVllmAsst = (assistantId: string) =>
    openConfirmModal({
      title: 'Удалить ассистента (vLLM)?',
      children: <Text size="sm">ID: {idShort(assistantId, 40)}</Text>,
      confirmProps: { color: 'red' },
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      onConfirm: () =>
        void api
          .deleteVLLMAssistantResource(projectId, assistantId)
          .then(() => {
            showNotification({ title: 'Удалено', message: '', color: 'green' })
            onResourcesChanged()
            return load()
          })
          .catch(notifyErr),
    })

  const viewAssistant = (assistantId: string) => {
    if (isOpenAI) {
      setDetailsJson(null)
      setDetailsLoading(true)
      void api
        .assistantDetails(projectId, assistantId)
        .then((raw) => {
          setDetailsJson(JSON.stringify(raw, null, 2))
        })
        .catch(notifyErr)
        .finally(() => setDetailsLoading(false))
    }
  }

  const vectorStoresOpenAI: OpenAIResourceVectorStore[] = openaiData?.vector_stores ?? []
  const assistantsOpenAI: OpenAIResourceAssistant[] = openaiData?.assistants ?? []
  const vectorStoresVllm = vllmData?.vector_stores ?? []
  const assistantsVllm = vllmData?.assistants ?? []

  const emptyOpenAI =
    isOpenAI && vectorStoresOpenAI.length === 0 && assistantsOpenAI.length === 0
  const emptyVllm = isVllm && vectorStoresVllm.length === 0 && assistantsVllm.length === 0
  const openaiNeedsSetup = isOpenAI && openaiData?.openai_configured === false
  const vllmNeedsSetup = isVllm && vllmData?.vllm_configured === false

  return (
    <Stack gap="md">
      <Group>
        <Button size="sm" variant="default" loading={loading} onClick={() => void load()}>
          Обновить
        </Button>
      </Group>

      {error ? (
        <Text size="sm" c="red">
          {error}
        </Text>
      ) : null}

      {loading && resourcesSupported && !openaiData && !vllmData ? (
        <Text size="sm" c="dimmed">
          Загрузка…
        </Text>
      ) : null}

      {!loading && !error && !resourcesSupported ? (
        <Text size="sm" c="dimmed">
          Список Vector Store и Assistants в OpenAI/vLLM для провайдера «{provider}» здесь не запрашивается.
          Переключите провайдера на OpenAI или vLLM на вкладке «Основные», если нужен этот просмотр.
        </Text>
      ) : null}

      {!loading && !error && isVllm && vllmNeedsSetup ? (
        <Text size="sm" c="dimmed">
          vLLM не настроен: укажите URL шлюза и API-ключ на вкладке «Основные», либо выберите провайдера vLLM.
        </Text>
      ) : null}

      {!loading && !error && isVllm && !vllmNeedsSetup && emptyVllm ? (
        <Text size="sm" c="dimmed">
          Нет ресурсов vLLM. Создайте Vector Store или Assistant через синхронизацию в основных настройках.
        </Text>
      ) : null}

      {!loading && !error && isOpenAI && openaiNeedsSetup ? (
        <Text size="sm" c="dimmed">
          OpenAI не настроен: выберите провайдера OpenAI и укажите API-ключ на вкладке «Основные».
        </Text>
      ) : null}

      {!loading && !error && isOpenAI && !openaiNeedsSetup && emptyOpenAI ? (
        <Text size="sm" c="dimmed">
          Нет ресурсов OpenAI. Создайте Vector Store или Assistant через синхронизацию.
        </Text>
      ) : null}

      {isOpenAI && (vectorStoresOpenAI.length > 0 || assistantsOpenAI.length > 0) ? (
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <Stack gap="sm">
            <Text fw={600}>Vector Stores ({vectorStoresOpenAI.length})</Text>
            {vectorStoresOpenAI.length === 0 ? (
              <Text size="sm" c="dimmed">
                Нет Vector Stores
              </Text>
            ) : (
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>Название</Table.Th>
                    <Table.Th>Статус</Table.Th>
                    <Table.Th>Создан</Table.Th>
                    <Table.Th w={100} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {vectorStoresOpenAI.map((vs) => (
                    <Table.Tr key={vs.id}>
                      <Table.Td style={{ fontFamily: 'monospace', fontSize: 11 }}>{idShort(vs.id, 24)}</Table.Td>
                      <Table.Td>{vs.name || '—'}</Table.Td>
                      <Table.Td>{vs.status || '—'}</Table.Td>
                      <Table.Td>{formatDateTimeDot(vs.created_at)}</Table.Td>
                      <Table.Td>
                        {canEdit ? (
                          <Button size="compact-xs" color="red" variant="light" onClick={() => deleteOpenAIvs(vs.id)}>
                            Удалить
                          </Button>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
          <Stack gap="sm">
            <Text fw={600}>Assistants ({assistantsOpenAI.length})</Text>
            {assistantsOpenAI.length === 0 ? (
              <Text size="sm" c="dimmed">
                Нет Assistants
              </Text>
            ) : (
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>Название</Table.Th>
                    <Table.Th>Модель</Table.Th>
                    <Table.Th>Создан</Table.Th>
                    <Table.Th w={200} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {assistantsOpenAI.map((asst) => (
                    <Table.Tr key={asst.id}>
                      <Table.Td style={{ fontFamily: 'monospace', fontSize: 11 }}>{idShort(asst.id, 24)}</Table.Td>
                      <Table.Td>{asst.name || '—'}</Table.Td>
                      <Table.Td>{asst.model || '—'}</Table.Td>
                      <Table.Td>{formatDateTimeDot(asst.created_at)}</Table.Td>
                      <Table.Td>
                        <Group gap={6}>
                          <Button size="compact-xs" variant="default" onClick={() => viewAssistant(asst.id)}>
                            Просмотр
                          </Button>
                          {canEdit ? (
                            <Button
                              size="compact-xs"
                              color="red"
                              variant="light"
                              onClick={() => deleteOpenAIasst(asst.id)}
                            >
                              Удалить
                            </Button>
                          ) : null}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </SimpleGrid>
      ) : null}

      {isVllm && !vllmNeedsSetup && (vectorStoresVllm.length > 0 || assistantsVllm.length > 0) ? (
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <Stack gap="sm">
            <Text fw={600}>Vector Stores ({vectorStoresVllm.length})</Text>
            {vectorStoresVllm.length === 0 ? (
              <Text size="sm" c="dimmed">
                Нет Vector Stores
              </Text>
            ) : (
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>Название</Table.Th>
                    <Table.Th>Qdrant</Table.Th>
                    <Table.Th>Создан</Table.Th>
                    <Table.Th w={100} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {vectorStoresVllm.map((vs) => (
                    <Table.Tr key={vs.id}>
                      <Table.Td style={{ fontFamily: 'monospace', fontSize: 11 }}>{idShort(vs.id, 24)}</Table.Td>
                      <Table.Td>{vs.name || '—'}</Table.Td>
                      <Table.Td>{vs.qdrant_collection || '—'}</Table.Td>
                      <Table.Td>{formatDateTimeDot(vs.created_at)}</Table.Td>
                      <Table.Td>
                        {canEdit ? (
                          <Button size="compact-xs" color="red" variant="light" onClick={() => deleteVllmVs(vs.id)}>
                            Удалить
                          </Button>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
          <Stack gap="sm">
            <Text fw={600}>Assistants ({assistantsVllm.length})</Text>
            {assistantsVllm.length === 0 ? (
              <Text size="sm" c="dimmed">
                Нет Assistants
              </Text>
            ) : (
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>Название</Table.Th>
                    <Table.Th>Создан</Table.Th>
                    <Table.Th w={100} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {assistantsVllm.map((asst) => (
                    <Table.Tr key={asst.id}>
                      <Table.Td style={{ fontFamily: 'monospace', fontSize: 11 }}>{idShort(asst.id, 24)}</Table.Td>
                      <Table.Td>{asst.name || '—'}</Table.Td>
                      <Table.Td>{formatDateTimeDot(asst.created_at)}</Table.Td>
                      <Table.Td>
                        {canEdit ? (
                          <Button size="compact-xs" color="red" variant="light" onClick={() => deleteVllmAsst(asst.id)}>
                            Удалить
                          </Button>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </SimpleGrid>
      ) : null}

      <AppModal
        opened={detailsJson != null || detailsLoading}
        onClose={() => {
          setDetailsJson(null)
          setDetailsLoading(false)
        }}
        title="Детали Assistant"
        size="xl"
      >
        {detailsLoading ? (
          <InlineLoader label="Загрузка…" />
        ) : detailsJson ? (
          <ScrollArea.Autosize mah={480}>
            <Code block style={{ whiteSpace: 'pre-wrap' }}>
              {detailsJson}
            </Code>
          </ScrollArea.Autosize>
        ) : null}
      </AppModal>
    </Stack>
  )
}
